import { useEffect, useMemo, useState } from 'react';
import { clientPortalApi, type CoverageEntitiesResult, type RouteDirection, type RouteMetricsResult } from '../api';

export const DEFAULT_WALK_TIME_MIN = 5;
export const DEFAULT_WALKING_SPEED_KMH = 4.5;

/**
 * Único punto de fetch de route-metrics + coverage-entities — usado por
 * Ficha (parámetros de caminata fijos) y Visor (ajustables). Misma
 * lógica IDA/VUELTA/CIRCUIT, mismos endpoints, un solo lugar.
 */
export function useRouteTerritorialData(args: {
  slug: string;
  token: string;
  analysisId: string;
  routeMode: 'CIRCUIT' | null;
  ida: [number, number][];
  vuelta: [number, number][];
  walkTimeMinutes?: number;
  walkingSpeedKmh?: number;
}): {
  direction: RouteDirection;
  setDirection: (d: RouteDirection) => void;
  isCircuit: boolean;
  routeCoordinates: [number, number][];
  routeMetrics: RouteMetricsResult | null;
  coverageEntities: CoverageEntitiesResult | null;
  error: string | null;
} {
  const { slug, token, analysisId, routeMode, ida, vuelta } = args;
  const walkTimeMinutes = args.walkTimeMinutes ?? DEFAULT_WALK_TIME_MIN;
  const walkingSpeedKmh = args.walkingSpeedKmh ?? DEFAULT_WALKING_SPEED_KMH;
  const isCircuit = routeMode === 'CIRCUIT';

  const [direction, setDirection] = useState<RouteDirection>(isCircuit ? 'CIRCUITO' : 'IDA');
  const [routeMetrics, setRouteMetrics] = useState<RouteMetricsResult | null>(null);
  const [coverageEntities, setCoverageEntities] = useState<CoverageEntitiesResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setRouteMetrics(null);
    setCoverageEntities(null);
    setError(null);
    Promise.all([
      clientPortalApi.getRouteMetrics(slug, analysisId, token, direction),
      clientPortalApi.getCoverageEntities(slug, analysisId, token, direction, { walkTimeMinutes, walkingSpeedKmh }),
    ])
      .then(([rm, ce]) => {
        if (cancelled) return;
        setRouteMetrics(rm);
        setCoverageEntities(ce);
      })
      .catch(() => {
        if (!cancelled) setError('No se pudieron calcular las métricas territoriales de esta variante.');
      });
    return (): void => {
      cancelled = true;
    };
  }, [slug, analysisId, token, direction, walkTimeMinutes, walkingSpeedKmh]);

  const routeCoordinates = useMemo<[number, number][]>(() => {
    if (direction === 'CIRCUITO' || direction === 'IDA') return ida;
    return vuelta;
  }, [direction, ida, vuelta]);

  return { direction, setDirection, isCircuit, routeCoordinates, routeMetrics, coverageEntities, error };
}
